import { haptic } from '$lib/lib.js';

/**
 * Drag-scrubbing for number inputs.
 *
 * A tap (no significant horizontal movement) keeps native behavior: the
 * input gets focused and the on-screen keyboard opens. A horizontal
 * finger/pen drag turns the input into a slider: each ~10px of movement
 * adds or removes one step, with a haptic tick on every step change.
 *
 * Touch/pen only — mouse drag is left fully native on desktop.
 *
 * @param {HTMLInputElement} node
 * @param {{
 *   getStep: () => number,
 *   getMin?: () => number | null,
 *   getMax?: () => number | null,
 *   getSiblingId?: () => string | null,
 *   siblingRole?: 'min' | 'max',
 *   threshold?: number,
 *   pxPerStep?: number,
 * }} params
 */
export function scrubNumber(node, params) {
  const THRESHOLD = params.threshold ?? 10;
  const PX_PER_STEP = params.pxPerStep ?? 10;

  let startX = 0;
  let baseValue = 0;
  let baseSteps = 0;
  let scrubbed = false;
  let pointerId = -1;

  /**
   * @param {number} value
   * @param {'min'|'max'} role
   * @param {HTMLInputElement | null} sibling
   */
  function clampToSibling(value, role, sibling) {
    if (!sibling) {
      return value;
    }
    const s = parseFloat(sibling.value);
    if (Number.isNaN(s)) {
      return value;
    }
    return role === 'min' ? Math.min(value, s) : Math.max(value, s);
  }

  /** @param {Event} event */
  function suppressFocus(event) {
    event.preventDefault();
  }

  /** @param {PointerEvent} event */
  function onDown(event) {
    if (event.pointerType === 'mouse' || pointerId !== -1) {
      return;
    }
    const step = params.getStep();
    if (!(step > 0)) {
      return;
    }
    pointerId = event.pointerId;
    startX = event.clientX;
    scrubbed = false;
    const current = parseFloat(node.value);
    const start = Number.isNaN(current) ? (params.getMin?.() ?? 0) : current;
    baseValue = Math.round(start / step) * step;
    baseSteps = 0;
    node.setPointerCapture(event.pointerId);
  }

  /** @param {PointerEvent} event */
  function onMove(event) {
    if (event.pointerId !== pointerId) {
      return;
    }
    const dx = event.clientX - startX;
    if (!scrubbed && Math.abs(dx) < THRESHOLD) {
      return;
    }
    if (!scrubbed) {
      // Horizontal intent detected: take over the gesture and keep the
      // caret from being dragged through the field's text.
      scrubbed = true;
      node.addEventListener('click', suppressFocus, { capture: true, once: true });
    }
    event.preventDefault();

    const step = params.getStep();
    const steps = Math.round(dx / PX_PER_STEP);
    if (steps === baseSteps) {
      return;
    }

    let next = baseValue + steps * step;
    const min = params.getMin?.();
    if (min !== null && min !== undefined) {
      next = Math.max(next, min);
    }
    const max = params.getMax?.();
    if (max !== null && max !== undefined) {
      next = Math.min(next, max);
    }
    /** @type {HTMLInputElement | null} */
    const sibling = params.getSiblingId
      ? (/** @type {HTMLInputElement | null} */ (
          document.getElementById(/** @type {string} */ (params.getSiblingId()))
        ))
      : null;
    next = clampToSibling(
      next,
      /** @type {'min'|'max'} */ (params.siblingRole ?? 'min'),
      sibling
    );

    if (next !== parseFloat(node.value)) {
      node.value = String(next);
      haptic('selectionChanged');
    }
    baseSteps = steps;
  }

  /** @param {PointerEvent} event */
  function onUp(event) {
    if (event.pointerId !== pointerId) {
      return;
    }
    pointerId = -1;
    if (scrubbed) {
      // The pointerup right after a drag would otherwise focus the input
      // and pop up the on-screen keyboard; swallow it so a scrub stays a
      // scrub and only a clean tap opens the keyboard.
      event.preventDefault();
      node.addEventListener('pointerup', suppressFocus, { capture: true, once: true });
    }
  }

  /** @param {PointerEvent} event */
  function onCancel(event) {
    if (event.pointerId !== pointerId) {
      return;
    }
    pointerId = -1;
    scrubbed = false;
  }

  node.addEventListener('pointerdown', onDown);
  node.addEventListener('pointermove', onMove);
  node.addEventListener('pointerup', onUp);
  node.addEventListener('pointercancel', onCancel);

  return {
    destroy() {
      node.removeEventListener('pointerdown', onDown);
      node.removeEventListener('pointermove', onMove);
      node.removeEventListener('pointerup', onUp);
      node.removeEventListener('pointercancel', onCancel);
    }
  };
}
